/**
 * Configuración de URLs para API y archivos media.
 * REACT_APP_API_BASE_URL: URL base del endpoint de volcanes (ej: http://localhost:8000/api/volcanes)
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/**
 * Obtiene la URL completa para archivos media (imágenes, KMZ, etc.).
 * El backend suele devolver rutas como /media/archivo.png.
 * Usa el origen del API_BASE para construir la URL correcta.
 * @param {string} path - Ruta relativa (ej: /media/mapa.png) o URL completa
 * @returns {string} URL completa del recurso
 */
export function getMediaUrl(path) {
  if (!path) return "";
  // Si ya es URL absoluta, devolver tal cual
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  try {
    const origin = new URL(API_BASE).origin;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${origin}${normalizedPath}`;
  } catch {
    return path;
  }
}

/**
 * Normaliza objetos de imagen del API (original + vista_previa) o rutas legacy.
 */
export function getImagenUrls(imagen) {
  if (!imagen) return { preview: null, original: null };
  if (typeof imagen === "string") {
    return { preview: imagen, original: imagen };
  }
  const preview = imagen?.vista_previa ?? null;
  const original = imagen?.original ?? preview ?? null;
  return { preview, original };
}

export function hasImagen(imagen) {
  const { preview, original } = getImagenUrls(imagen);
  return Boolean(preview || original);
}

export function getImagenPreviewUrl(imagen) {
  const { preview, original } = getImagenUrls(imagen);
  const path = preview || original;
  return path ? getMediaUrl(path) : "";
}

export function getImagenOriginalUrl(imagen) {
  const { original, preview } = getImagenUrls(imagen);
  const path = original || preview;
  return path ? getMediaUrl(path) : "";
}

export function resolveBackendRelativeUrlsInHtml(html) {
  if (!html || typeof html !== "string" || !API_BASE) return html;
  let origin;
  try {
    origin = new URL(API_BASE).origin;
  } catch {
    return html;
  }
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("[href],[src]").forEach((el) => {
      for (const attr of ["href", "src"]) {
        if (!el.hasAttribute(attr)) continue;
        const v = el.getAttribute(attr);
        if (
          !v ||
          v.startsWith("http://") ||
          v.startsWith("https://") ||
          v.startsWith("mailto:") ||
          v.startsWith("#") ||
          v.startsWith("data:")
        ) {
          continue;
        }
        if (v.startsWith("/")) el.setAttribute(attr, `${origin}${v}`);
      }
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

export { API_BASE };
